import type { CogniteClient } from '@cognite/sdk';
import { NavigationProgress } from '@mantine/nprogress';
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet, retainSearchParams, useRouterState } from '@tanstack/react-router';
import { use } from 'react';

import classes from './__root.module.css';
import backgroundVideo from '#assets/video/background.mp4?url';
import { ProjectContext } from '#contexts/ProjectContext';
import 'dayjs/locale/en';

import { camelToKebabCase, useMantineTheme } from '@mantine/core';
import { zodValidator } from '@tanstack/zod-adapter';
import z from 'zod';

import { Devtools } from '#components/layout/Devtools';
import { Sidebar } from '#components/layout/Sidebar';
import { useProgressTracker } from '#hooks/useProgressTracker';
import { env } from '#lib/env';

export interface RouteContext {
  getTitle?: () => string;
  queryClient: QueryClient;
  cogniteClient: CogniteClient;
}

export const Route = createRootRouteWithContext<{
  getTitle?: () => string;
  queryClient: QueryClient;
  cogniteClient: CogniteClient;
}>()({
  component: RouteComponent,
  validateSearch: zodValidator(
    z.object({
      prompts: z.string().array().optional(),
      step: z.number().optional().catch(0),
    }),
  ),
  search: {
    middlewares: [retainSearchParams(['prompts', 'step'])],
  },
});

function RouteComponent() {
  const { config } = use(ProjectContext);
  const matches = useRouterState({ select: (state) => state.matches });
  const theme = useMantineTheme();

  const breadcrumbs = matches
    .filter((match) => match.context?.getTitle)
    .map(({ pathname, context }) => ({
      title: context?.getTitle?.(),
      path: pathname,
    }));

  const pageTitle = [`${[...new Set(breadcrumbs.toReversed().map(({ title }) => title))].join(' / ')}`, env.appName]
    .filter(Boolean)
    .join(' – ');

  useProgressTracker();

  return (
    <div className={classes.appRoot}>
      <title>{pageTitle}</title>
      <style>{`:root { ${getThemeVariables(theme.other)
        .map(([key, value]) => `${key}: ${value};`)
        .join('\n')} }`}</style>
      {config?.faviconUrl !== undefined && <link rel="icon" href={config.faviconUrl} />}

      <div className={classes.backdrop}>
        <video className={classes.backgroundVideo} autoPlay muted loop playsInline>
          <source src={backgroundVideo} type="video/mp4" />
        </video>
      </div>

      <NavigationProgress color="dark.3" />

      <Sidebar />

      <main className={classes.content}>
        <Outlet />
      </main>

      <Devtools />
    </div>
  );
}

function getThemeVariables(obj: Record<string, unknown>, prefix = '--theme'): [string, string | number][] {
  return Object.entries(obj).flatMap(([key, value]) => {
    const kebabKey = camelToKebabCase(key);
    const variableName = `${prefix}-${kebabKey}`;

    if (Array.isArray(value)) {
      return getThemeVariables(Object.fromEntries(value.map((v, i) => [i + 1, v])), variableName);
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      return getThemeVariables(value as Record<string, unknown>, variableName);
    }

    return [[variableName, value]] as [string, string | number][];
  });
}
